import React, { memo, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import type { JsmProjectMenuProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-jsm-project-menu/src/ui/project-menu/JsmProjectMenu.tsx';
import { Skeleton as SkeletonOld } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-project-common/src/common/ui/skeleton/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import node, {
	type JsmProjectMenuContent_SidebarQuery,
} from '@atlassian/jira-relay/src/__generated__/JsmProjectMenuContent_SidebarQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition';
import { fg } from '@atlassian/jira-feature-gating';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';

const SingleMenuItemSkeleton = () => <SkeletonNavMenuItems count={1} />;

const Skeleton = componentWithCondition(
	() => fg('jfp-magma-proj-menu-fix'),
	SingleMenuItemSkeleton,
	SkeletonOld,
);

const LazySidebarContent = lazyForPaint(() =>
	import(
		/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-jsm-project-menu" */ './ui/project-menu'
	).then(({ JsmProjectMenu }) => JsmProjectMenu),
);

const AsyncJsmProjectMenuContent = memo(
	({ projectData, spotlight, menuItemButtonRef }: JsmProjectMenuProps) => {
		const { data: projectContextData } = useProjectContext();
		const projectId = projectContextData?.projectId;
		const cloudId = useCloudId();

		const [queryRef, loadQuery, disposeQuery] =
			useQueryLoader<JsmProjectMenuContent_SidebarQuery>(node);

		useEffect(() => {
			if (!queryRef) {
				loadQuery({ cloudId, projectId: String(projectId) });
			}
		}, [queryRef, loadQuery, cloudId, projectId]);

		useEffect(() => {
			return () => {
				disposeQuery();
			};
		}, [disposeQuery]);

		return (
			<Placeholder name="nav4.sidebar.jsm-project-menu.loading-lazy" fallback={<Skeleton />}>
				{queryRef && (
					<LazySidebarContent
						projectData={projectData}
						spotlight={spotlight}
						menuItemButtonRef={menuItemButtonRef}
						queryRef={queryRef ?? undefined}
					/>
				)}
			</Placeholder>
		);
	},
);

const AsyncJsmProjectMenuOld = memo(
	({ projectData, spotlight, menuItemButtonRef }: JsmProjectMenuProps) => (
		<SidebarBoundary
			id="nav4.sidebar.jsm-project-menu"
			packageName="jiraNavigationAppsSidebarNav4JsmProjectMenu"
			teamName="jsm-getting-started"
			ufoName="nav4.sidebar.jsm-project-menu"
		>
			<Placeholder name="nav4.sidebar.jsm-project-menu.loading-lazy" fallback={<Skeleton />}>
				<LazySidebarContent
					projectData={projectData}
					spotlight={spotlight}
					menuItemButtonRef={menuItemButtonRef}
				/>
			</Placeholder>
		</SidebarBoundary>
	),
);

const AsyncJsmProjectMenuNew = memo(
	({ projectData, spotlight, menuItemButtonRef }: JsmProjectMenuProps) => (
		<SidebarBoundary
			id="nav4.sidebar.jsm-project-menu"
			packageName="jiraNavigationAppsSidebarNav4JsmProjectMenu"
			teamName="jsm-getting-started"
			ufoName="nav4.sidebar.jsm-project-menu"
		>
			<AsyncJsmProjectMenuContent
				projectData={projectData}
				spotlight={spotlight}
				menuItemButtonRef={menuItemButtonRef}
			/>
		</SidebarBoundary>
	),
);

export const AsyncJsmProjectMenu = componentWithCondition(
	() => fg('more_for_nav_4_level_3'),
	AsyncJsmProjectMenuNew,
	AsyncJsmProjectMenuOld,
);
